/* PiHOT long-form pages — sections/pihot-main-page.liquid
   - [data-normalize]: headings pasted in ALL CAPS are shown in sentence case and lose
     trailing colons. Display only; the page content in the admin is not changed.
   - [data-toc]: builds "On this page" from the top heading level in the content and
     marks the section being read. The <details> stays open from 1068px; below that it
     is collapsed and closes after a jump (before scrolling, so the target lands right). */

(() => {
  if (customElements.get('p-page')) return;

  const DESKTOP = '(min-width: 1068px)';
  const KEEP = new Map(
    ['PiHOT', 'EU', 'EEA', 'UK', 'US', 'USA', 'FAQ', 'GDPR', 'CCPA', 'VAT', 'DHL', 'UPS', 'IP', 'ID', 'SMS', 'PayPal'].map(
      (word) => [word.toLowerCase(), word]
    )
  );

  const textNodes = (el) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  };

  const isShouting = (text) => {
    const letters = text.replace(/[^A-Za-z]/g, '');
    if (letters.length < 4) return false;
    return letters.replace(/[^A-Z]/g, '').length / letters.length >= 0.6;
  };

  const tidyHeading = (heading) => {
    const nodes = textNodes(heading).filter((node) => node.nodeValue.trim());
    if (!nodes.length) return;

    if (isShouting(heading.textContent)) {
      let first = true;
      nodes.forEach((node) => {
        let text = node.nodeValue
          .toLowerCase()
          .replace(/[a-z]+/g, (word) => KEEP.get(word) || word)
          .replace(/([-–—:]\s+)([a-z])/g, (match, sep, letter) => sep + letter.toUpperCase());
        if (first) {
          text = text.replace(/[a-z]/i, (letter) => letter.toUpperCase());
          first = false;
        }
        node.nodeValue = text;
      });
    }

    const last = nodes[nodes.length - 1];
    last.nodeValue = last.nodeValue.replace(/\s*[:：]\s*$/, '');
  };

  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/[\s_-]+/g, '-')
      .slice(0, 60) || 'section';

  class PiHOTPage extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('.p-page__body');
      if (!this.body) return;

      this.revealCurrentLink();
      this.removeEmptyParagraphs();
      const headings = [...this.body.querySelectorAll('h1, h2, h3, h4, h5, h6')].filter((h) => h.textContent.trim());
      if (this.hasAttribute('data-normalize')) headings.forEach(tidyHeading);

      this.toc = this.querySelector('[data-p-page-toc]');
      if (this.toc && this.hasAttribute('data-toc')) this.buildToc(headings);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.mq?.removeEventListener('change', this.onBreakpoint);
      cancelAnimationFrame(this.frame);
    }

    /* Below 1068px the policy links are a horizontal pill row; start it at the
       current page instead of the first link. */
    revealCurrentLink() {
      const list = this.querySelector('.p-page__nav-list');
      const current = list?.querySelector('[aria-current="page"]');
      if (!current || list.scrollWidth <= list.clientWidth) return;
      const item = current.getBoundingClientRect();
      const box = list.getBoundingClientRect();
      list.scrollLeft += item.left - box.left - (list.clientWidth - item.width) / 2;
    }

    removeEmptyParagraphs() {
      this.body.querySelectorAll('p').forEach((p) => {
        if (p.textContent.trim()) return;
        if (p.querySelector(':not(br, span, meta, strong, b, em, i)')) return;
        p.remove();
      });
    }

    buildToc(headings) {
      const top = Math.min(...headings.map((h) => Number(h.tagName[1])));
      this.headings = headings.filter((h) => Number(h.tagName[1]) === top);
      if (this.headings.length < 2) {
        this.toc.hidden = true;
        return;
      }

      this.toc.hidden = false;
      this.toc.closest('.p-page__aside').hidden = false;
      this.closest('.p-page').classList.add('p-page--aside');

      const used = new Set([...document.querySelectorAll('[id]')].map((el) => el.id));
      const list = this.toc.querySelector('.p-page__toc-list');
      list.textContent = '';
      this.links = this.headings.map((heading) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          let id = slug(heading.textContent);
          for (let n = 2; used.has(id); n += 1) id = `${slug(heading.textContent)}-${n}`;
          heading.id = id;
          used.add(id);
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'p-page__toc-link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', (event) => this.jump(event, heading));
        item.append(link);
        list.append(item);
        return link;
      });

      this.mq = window.matchMedia(DESKTOP);
      this.onBreakpoint = () => {
        this.toc.open = this.mq.matches;
        this.toc.querySelector('summary').tabIndex = this.mq.matches ? -1 : 0;
      };
      this.onBreakpoint();
      this.mq.addEventListener('change', this.onBreakpoint);
      this.toc.addEventListener('toggle', () => {
        if (this.mq.matches && !this.toc.open) this.toc.open = true;
      });

      this.current = -1;
      this.frame = 0;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.markCurrent();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.markCurrent();

      const target = location.hash && this.headings.find((h) => `#${h.id}` === location.hash || `#${encodeURIComponent(h.id)}` === location.hash);
      if (target) requestAnimationFrame(() => target.scrollIntoView());
    }

    jump(event, heading) {
      event.preventDefault();
      if (!this.mq.matches) this.toc.open = false;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
      history.replaceState(null, '', `#${heading.id}`);
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }

    markCurrent() {
      const offset = parseFloat(getComputedStyle(this.headings[0]).scrollMarginTop) + 8;
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let index = 0;
      if (atEnd && this.headings[this.headings.length - 1].getBoundingClientRect().top < window.innerHeight) {
        index = this.headings.length - 1;
      } else {
        this.headings.forEach((heading, i) => {
          if (heading.getBoundingClientRect().top <= offset) index = i;
        });
      }
      if (index === this.current) return;
      this.current = index;
      this.links.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('p-page', PiHOTPage);
})();
